/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { SelectableTile } from 'carbon-components-react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { ServiceDeskLogo } from '../../../helpers/serviceDeskHelpers';
import { useSMSFeatures } from '../../../hooks/useChannelFeatures';
import { useChannelHelpers } from '../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  smsServiceDeskNames,
  smsProviderURLs,
  SMS_CHANNEL_TYPE,
  smsProviderToSMSChannelType
} from '../../../util/channelTypes';
import { QuickSetupComponentProps } from '../../../util/types';
import { FIELDS } from '../schema';
import { SelectProviderStepMessages as messages } from './messages';

export const SelectProviderStep = ({
  readOnly,
  markStepComplete
}: QuickSetupComponentProps) => {
  const supportedChannels = useSMSFeatures();
  const formMethods = useFormContext();

  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);
  const { updateIntegrationType } = useChannelHelpers(formMethods);
  const selectedProvider = getFieldValue(FIELDS.PROVIDER_TYPE);

  // Select which service desk is to be used.
  const onTypeChanged = ({ selectedItem }) => {
    updateFieldValue(FIELDS.PROVIDER_TYPE, selectedItem);
    updateFieldValue(FIELDS.PROVIDER_URL, smsProviderURLs[selectedItem]);
    // For SMS integration, store expects 4 different types (intelepper, genesys, twilio and byopsms) as of writting it today
    // UI creats a shell with type twilio first (as historically it there was a single type of provider for sms) and we are explicitely updating it
    // After selecting a provider type here
    updateIntegrationType(smsProviderToSMSChannelType[selectedItem]);
    markStepComplete(true);
  };

  return (
    supportedChannels.length > 0 && (
      <>
        <div className="QuickSetup__step-title">
          <FormattedMessage {...messages.title} />
        </div>
        <div className="TabContent__spacer8" />
        <div className="TabContent__subtitle">
          <FormattedMessage {...messages.description} />
        </div>
        <div className="TabContent__spacer32" />
        <div className="Connections-ServiceDesk__choose-new">
          <div className="Connections Connections-ServiceDesk__config">
            <div className="ServiceDesk__tiles ServiceDesk__tile-group">
              {supportedChannels.map(serviceDesk => (
                <SelectableTile
                  disabled={readOnly}
                  key={serviceDesk}
                  id={'service-desk-radio-' + serviceDesk}
                  className="ServiceDesk__tile"
                  selected={selectedProvider === serviceDesk}
                  value={selectedProvider}
                  onClick={e => {
                    if (!readOnly) {
                      e.preventDefault();
                      onTypeChanged({
                        selectedItem: serviceDesk
                      });
                    }
                  }}>
                  <ServiceDeskLogo
                    parentChannelType={SMS_CHANNEL_TYPE}
                    serviceDeskType={serviceDesk}
                    showTitle={false}
                  />
                  <p className="ServiceDesk__tile-title">
                    {smsServiceDeskNames[serviceDesk]}
                  </p>
                </SelectableTile>
              ))}
            </div>
          </div>
        </div>
      </>
    )
  );
};
